import React, {
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {Context} from '../contexts/WordList';
import {
  suggestTranslation,
  speechToText,
} from '../helpers/externalApi';
import {extractWordPairsFromText, extractWordPairsFromPage} from '../helpers/ollamaApi';
import {importUrl} from '../helpers/localApi';
import {useAiAvailable} from '../contexts/AiAvailability';
import {MiniKeyboard} from './MiniKeyboard';

export const AddWordForm = (props) => {
  const {displayConfirmPopup, onConfirmation, onBulkAdd, onClose} = props;

  const [state] = useContext(Context);
  const {language} = state;
  const firstInputRef = useRef(null);
  const wordFaInputRef = useRef(null);
  const {aiAvailable} = useAiAvailable();
  const formRef = useRef(null);

  const [suggestingTarget, setSuggestingTarget] = useState(false);
  const [suggestingSource, setSuggestingSource] = useState(false);
  const [suggestingTranslit, setSuggestingTranslit] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sttProcessing, setSttProcessing] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkSelectedKeys, setBulkSelectedKeys] = useState({});
  const [bulkIncludeTag, setBulkIncludeTag] = useState(false);
  const [bulkIncludeSentences, setBulkIncludeSentences] = useState(false);
  const [bulkUrl, setBulkUrl] = useState('');
  const [urlFetching, setUrlFetching] = useState(false);
  const [bulkSource, setBulkSource] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({
    stage: '',
    current: 0,
    total: 0,
    completed: 0,
    failed: 0,
    message: '',
    wordEn: '',
  });
  const [bulkError, setBulkError] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const keyboardDismissedRef = useRef(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (displayConfirmPopup && firstInputRef.current) {
      setTimeout(() => firstInputRef.current.focus(), 100);
    }
  }, [displayConfirmPopup]);

  // Close on Escape while the add-word modal is open (not mid bulk-save).
  useEffect(() => {
    if (!displayConfirmPopup) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !bulkSaving) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [displayConfirmPopup, bulkSaving, onClose]);

  // ── AI suggest target language + transliteration from English ──
  const handleSuggestTarget = useCallback(async () => {
    const form = formRef.current;
    if (!form) return;
    const wordEn = form.inputWordEn.value.trim();
    if (!wordEn) return;

    setSuggestingTarget(true);
    try {
      const langName =
        language === 'hebrew' ? 'Hebrew' : 'Farsi';
      const result = await suggestTranslation({
        word: wordEn,
        fromLang: 'English',
        toLang: langName,
      });
      if (result.translation) {
        form.inputWordFa.value = result.translation;
      }
      if (result.transliteration) {
        form.inputTransliteration.value =
          result.transliteration;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest failed:', err.message);
    } finally {
      setSuggestingTarget(false);
    }
  }, [language]);

  // ── AI suggest English from target language ───────────────────
  const handleSuggestSource = useCallback(async () => {
    const form = formRef.current;
    if (!form) return;
    const wordFa = form.inputWordFa.value.trim();
    if (!wordFa) return;

    setSuggestingSource(true);
    try {
      const langName =
        language === 'hebrew' ? 'Hebrew' : 'Farsi';
      const result = await suggestTranslation({
        word: wordFa,
        fromLang: langName,
        toLang: 'English',
      });
      if (result.translation) {
        form.inputWordEn.value = result.translation;
      }
      if (result.transliteration && !form.inputTransliteration.value.trim()) {
        form.inputTransliteration.value = result.transliteration;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest source failed:', err.message);
    } finally {
      setSuggestingSource(false);
    }
  }, [language]);

  // ── AI suggest transliteration from target language ────────────
  const handleSuggestTransliteration = useCallback(async () => {
    const form = formRef.current;
    if (!form) return;
    const wordFa = form.inputWordFa.value.trim();
    if (!wordFa) return;

    setSuggestingTranslit(true);
    try {
      const langName =
        language === 'hebrew' ? 'Hebrew' : 'Farsi';
      const result = await suggestTranslation({
        word: wordFa,
        fromLang: langName,
        toLang: 'English',
      });
      if (result.transliteration) {
        form.inputTransliteration.value = result.transliteration;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Suggest transliteration failed:', err.message);
    } finally {
      setSuggestingTranslit(false);
    }
  }, [language]);

  // Flag candidates already present in the loaded word list so the UI can
  // pre-deselect them. The server re-checks authoritatively on bulk save.
  const markDuplicates = useCallback((entries) => {
    const existingEn = new Set(
      state.items.map((w) => (w.wordEn || '').trim().toLowerCase()),
    );
    const existingFa = new Set(
      state.items.map((w) => (w.wordFa || '').trim()),
    );
    return entries.map((entry) => ({
      ...entry,
      duplicate:
        existingEn.has(entry.wordEn.trim().toLowerCase()) ||
        existingFa.has(entry.wordFa.trim()),
    }));
  }, [state.items]);

  const selectExtracted = useCallback((entries) => {
    setBulkEntries(entries);
    const nextSelection = {};
    entries.forEach((entry, index) => {
      // Duplicates stay visible but unchecked by default.
      nextSelection[`${entry.wordEn}-${entry.wordFa}-${index}`] = !entry.duplicate;
    });
    setBulkSelectedKeys(nextSelection);
  }, []);

  const handleExtractBulk = useCallback(async (sourceText) => {
    const text = (typeof sourceText === 'string' ? sourceText : bulkText).trim();
    if (!text) return;

    setBulkLoading(true);
    setBulkError('');
    try {
      const entries = markDuplicates(
        await extractWordPairsFromText({
          text,
          language,
        }),
      );
      if (!entries.length) {
        setBulkError('No vocabulary pairs were extracted from the text.');
      }
      selectExtracted(entries);
    } catch (err) {
      const message = err && err.message ? err.message : 'Ollama extraction failed.';
      setBulkError(message);
      setBulkEntries([]);
      setBulkSelectedKeys({});
    } finally {
      setBulkLoading(false);
    }
  }, [bulkText, language, markDuplicates, selectExtracted]);

  // Fetch a page server-side (CORS-safe, SSRF-guarded), then run Ollama
  // extraction over the cleaned text.
  const handleFetchUrl = useCallback(async () => {
    const url = bulkUrl.trim();
    if (!url || urlFetching) return;

    setUrlFetching(true);
    setBulkError('');
    try {
      const page = await importUrl({url});
      setBulkSource({url: page.url || url, title: page.title || ''});
      const entries = markDuplicates(
        await extractWordPairsFromPage({
          pageText: page.text || '',
          language,
        }),
      );
      if (!entries.length) {
        setBulkError('No vocabulary pairs were extracted from that page.');
      }
      selectExtracted(entries);
    } catch (err) {
      const message = err && err.message ? err.message : 'URL import failed.';
      setBulkError(message);
      setBulkEntries([]);
      setBulkSelectedKeys({});
      setBulkSource(null);
    } finally {
      setUrlFetching(false);
    }
  }, [bulkUrl, urlFetching, language, markDuplicates, selectExtracted]);

  const handleBulkAddWords = async () => {
    if (!bulkEntries.length || !onBulkAdd) return;

    const selectedEntries = bulkEntries.filter((entry, index) => {
      const key = `${entry.wordEn}-${entry.wordFa}-${index}`;
      return Boolean(bulkSelectedKeys[key]);
    }).map((entry) => ({
      wordEn: entry.wordEn,
      wordFa: entry.wordFa,
      transliteration: entry.transliteration,
      sourceUrl: bulkSource ? bulkSource.url : '',
      sourceTitle: bulkSource ? bulkSource.title : '',
    }));

    if (!selectedEntries.length) {
      setBulkError('Select at least one extracted word to add.');
      return;
    }

    setBulkError('');
    setBulkSaving(true);
    setBulkProgress({
      stage: 'starting',
      current: 0,
      total: selectedEntries.length,
      completed: 0,
      failed: 0,
      message: `Preparing to add ${selectedEntries.length} words...`,
      wordEn: '',
    });

    try {
      await onBulkAdd({
        entries: selectedEntries,
        options: {
          fillTag: bulkIncludeTag,
          fillSentences: bulkIncludeSentences,
        },
        onProgress: (progress) => {
          setBulkProgress((prev) => ({...prev, ...progress}));
        },
      });

      setBulkEntries([]);
      setBulkSelectedKeys({});
      setBulkText('');
      setBulkUrl('');
      setBulkSource(null);
      setBulkError('');
    } finally {
      setBulkSaving(false);
    }
  };

  const totalExtracted = bulkEntries.length;
  const selectedCount = bulkEntries.reduce((count, entry, index) => {
    const key = `${entry.wordEn}-${entry.wordFa}-${index}`;
    return bulkSelectedKeys[key] ? count + 1 : count;
  }, 0);
  const allSelected = totalExtracted > 0 && selectedCount === totalExtracted;

  const toggleBulkSelection = (entry, index) => {
    const key = `${entry.wordEn}-${entry.wordFa}-${index}`;
    setBulkSelectedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleSelectAllBulk = () => {
    if (allSelected) {
      setBulkSelectedKeys({});
      return;
    }

    const nextSelection = {};
    bulkEntries.forEach((entry, index) => {
      nextSelection[`${entry.wordEn}-${entry.wordFa}-${index}`] = true;
    });
    setBulkSelectedKeys(nextSelection);
  };

  // ── Voice input (STT) ────────────────────────────
  const handleMicToggle = useCallback(async () => {
    if (recording) {
      // stop
      if (mediaRecRef.current) {
        mediaRecRef.current.stop();
      }
      return;
    }

    try {
      const stream = await navigator.mediaDevices
        .getUserMedia({audio: true});
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, {
          type: 'audio/webm',
        });
        try {
          setSttProcessing(true);
          const text = await speechToText({audioBlob: blob});
          if (text && formRef.current) {
            formRef.current.inputWordFa.value = text;
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('STT failed:', err.message);
        } finally {
          setSttProcessing(false);
        }
      };

      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Microphone access denied:', err.message);
    }
  }, [recording]);

  const handleKeyboardClose = useCallback(() => {
    keyboardDismissedRef.current = true;
    setShowKeyboard(false);
  }, []);

  const toggleKeyboard = useCallback(() => {
    setShowKeyboard((prev) => {
      const next = !prev;
      keyboardDismissedRef.current = !next;
      if (next && wordFaInputRef.current) {
        setTimeout(() => wordFaInputRef.current.focus(), 0);
      }
      return next;
    });
  }, []);

  const handleWordFaFocus = useCallback(() => {
    if (!keyboardDismissedRef.current) setShowKeyboard(true);
  }, []);

  const handleConfirmation = async (e) => {
    e.preventDefault();
    const inputWordEn = e.target.inputWordEn.value;
    const inputWordFa = e.target.inputWordFa.value;
    const inputTransliteration =
      e.target.inputTransliteration.value;

    const hasManualValues =
      Boolean(inputWordEn.trim()) ||
      Boolean(inputWordFa.trim()) ||
      Boolean(inputTransliteration.trim());

    if (!hasManualValues && selectedCount > 0) {
      await handleBulkAddWords();
      return;
    }

    if (!inputWordEn.trim() || !inputWordFa.trim()) return;

    const cleanedInputWordEn =
      inputWordEn.charAt(0).toUpperCase() +
      inputWordEn.slice(1);
    const cleanedInputWordFa =
      inputWordFa.charAt(0).toUpperCase() +
      inputWordFa.slice(1);
    const cleanedInputTransliteration = inputTransliteration;

    e.target.inputWordEn.value = '';
    e.target.inputWordFa.value = '';
    e.target.inputTransliteration.value = '';

    onConfirmation(
      cleanedInputWordEn,
      cleanedInputWordFa,
      cleanedInputTransliteration,
    );
  };

  const handleClose = () => {
    onClose();
  };

  if (!displayConfirmPopup) {
    return null;
  }

  const langLabel =
    language === 'hebrew' ? 'Hebrew' : 'Farsi';
  const langFont =
    language === 'farsi' ? 'font-farsi' : 'font-hebrew';

  let aiStatusText = '';
  if (suggestingTarget) aiStatusText = `Generating ${langLabel} translation...`;
  else if (suggestingSource) aiStatusText = 'Generating English translation...';
  else if (suggestingTranslit) aiStatusText = 'Generating transliteration...';
  else if (recording) aiStatusText = 'Recording audio...';
  else if (sttProcessing) aiStatusText = 'Transcribing audio...';
  else if (urlFetching) aiStatusText = 'Fetching and parsing the page...';
  else if (bulkLoading) aiStatusText = 'Extracting vocabulary with Ollama...';
  else if (bulkSaving) aiStatusText = bulkProgress.message || 'Adding extracted words to the database...';

  const bulkProgressPercent = bulkProgress.total > 0 ?
    Math.round((bulkProgress.current / bulkProgress.total) * 100) :
    0;

  return (
    <div className="overlay" onClick={handleClose}>
      <div
        className="modal"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="modal-title">Add New Word</h2>
        <p className="modal-subtitle">
          Enter the word in both languages to add
          it to your vocabulary.
        </p>
        {aiStatusText && (
          <div className="ai-progress-banner" role="status" aria-live="polite">
            <span className="ai-progress-dot" aria-hidden="true" />
            <span>{aiStatusText}</span>
          </div>
        )}
        <form
          ref={formRef}
          onSubmit={handleConfirmation}
          className="form-col"
        >
          <div className="form-group">
            <label className="form-label">
              English <span className="required">*</span>
            </label>
            <div className="row">
              <input
                className="field"
                placeholder="e.g. Hello"
                name="inputWordEn"
                ref={firstInputRef}
                autoComplete="off"
                style={{flex: 1}}
              />
              {aiAvailable && (
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleSuggestTarget}
                  disabled={suggestingTarget}
                  title="Fill target language from English"
                >
                  {suggestingTarget ? '⏳' : '✨'} To {langLabel}
                </button>
              )}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">
              {langLabel}{' '}
              <span className="required">*</span>
            </label>
            <div className="row">
              <input
                className={`field ${langFont}`}
                placeholder={`Translation in ${langLabel}`}
                name="inputWordFa"
                ref={wordFaInputRef}
                dir="rtl"
                autoComplete="off"
                onFocus={handleWordFaFocus}
                style={{flex: 1}}
              />
              <button
                type="button"
                className={`btn btn-sm mini-keyboard-toggle${showKeyboard ? ' btn-mic-active' : ''}`}
                onClick={toggleKeyboard}
                title={`Show ${langLabel} on-screen keyboard`}
                aria-pressed={showKeyboard}
              >
                ⌨
              </button>
              {aiAvailable && (
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleSuggestSource}
                  disabled={suggestingSource}
                  title="Fill English from target language"
                >
                  {suggestingSource ? '⏳' : '✨'} To EN
                </button>
              )}
              {aiAvailable && (
                <button
                  type="button"
                  className={`btn btn-sm btn-mic${recording ? ' btn-mic-active' : ''}`}
                  onClick={handleMicToggle}
                  title={recording ? 'Stop recording' : 'Voice input'}
                >
                  {recording ? '⏹' : '🎤'}
                </button>
              )}
            </div>
            {showKeyboard && (
              <MiniKeyboard
                language={language}
                inputRef={wordFaInputRef}
                onClose={handleKeyboardClose}
              />
            )}
          </div>
          <div className="form-group">
            <label className="form-label">
              Transliteration
            </label>
            <div className="row">
              <input
                className="field"
                placeholder="e.g. Shalom"
                name="inputTransliteration"
                autoComplete="off"
                style={{flex: 1}}
              />
              {aiAvailable && (
                <button
                  type="button"
                  className="btn btn-sm btn-ai"
                  onClick={handleSuggestTransliteration}
                  disabled={suggestingTranslit}
                  title="Suggest transliteration"
                >
                  {suggestingTranslit ? '⏳' : '✨'} Translit
                </button>
              )}
            </div>
            <span className="form-hint">
              How the word sounds in English letters
            </span>
          </div>

          {aiAvailable && (
          <div className="form-group bulk-import-panel">
            <label className="form-label">
              Import from URL (AI)
            </label>
            <div className="row">
              <input
                className="field"
                placeholder="https://example.com/article"
                name="inputBulkUrl"
                type="url"
                dir="ltr"
                autoComplete="off"
                value={bulkUrl}
                onChange={(e) => setBulkUrl(e.target.value)}
                style={{flex: 1}}
              />
              <button
                type="button"
                className="btn btn-sm btn-ai"
                onClick={handleFetchUrl}
                disabled={urlFetching || bulkLoading || bulkSaving || !bulkUrl.trim()}
                title="Fetch the page and extract vocabulary with Ollama"
              >
                {urlFetching ? '⏳' : '🌐'} Fetch &amp; Extract
              </button>
            </div>
            {bulkSource && (
              <span className="form-hint bulk-source-hint">
                Source: {bulkSource.title || bulkSource.url}
              </span>
            )}
            <label className="form-label" style={{marginTop: 10}}>
              Paste Text (Ollama Import)
            </label>
            <textarea
              className="field field-textarea"
              placeholder="Paste a paragraph or list. Ollama will extract vocabulary pairs."
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
            />
            <div className="row">
              <button
                type="button"
                className="btn btn-sm btn-ai"
                onClick={handleExtractBulk}
                disabled={bulkLoading || !bulkText.trim()}
              >
                {bulkLoading ? '⏳' : '✨'} Extract With Ollama
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleBulkAddWords}
                disabled={!bulkEntries.length || bulkSaving || selectedCount === 0}
              >
                {bulkSaving ? '⏳ Adding...' : `Add ${selectedCount || ''} Words`}
              </button>
            </div>
            {bulkEntries.length > 0 && (
              <div className="bulk-options-grid">
                <label className="bulk-option-toggle">
                  <input
                    type="checkbox"
                    checked={bulkIncludeTag}
                    onChange={(e) => setBulkIncludeTag(e.target.checked)}
                    disabled={bulkSaving}
                  />
                  <span>Auto-fill tag</span>
                </label>
                <label className="bulk-option-toggle">
                  <input
                    type="checkbox"
                    checked={bulkIncludeSentences}
                    onChange={(e) => setBulkIncludeSentences(e.target.checked)}
                    disabled={bulkSaving}
                  />
                  <span>Auto-fill sample sentences</span>
                </label>
              </div>
            )}
            {bulkError && (
              <span className="form-hint" style={{color: 'var(--red)'}}>
                {bulkError}
              </span>
            )}
            {bulkSaving && bulkProgress.total > 0 && (
              <div className="bulk-save-progress" role="status" aria-live="polite">
                <div className="bulk-save-progress-row">
                  <strong>{bulkProgress.completed}/{bulkProgress.total} added</strong>
                  <span>{bulkProgressPercent}%</span>
                </div>
                <div className="bulk-save-progress-track">
                  <div
                    className="bulk-save-progress-fill"
                    style={{width: `${bulkProgressPercent}%`}}
                  />
                </div>
                <div className="bulk-save-progress-meta">
                  {bulkProgress.wordEn ?
                    `Current: ${bulkProgress.wordEn}` :
                    bulkProgress.message}
                  {bulkProgress.failed > 0 && ` | Failed: ${bulkProgress.failed}`}
                </div>
              </div>
            )}
            {bulkEntries.length > 0 && (
              <div className="bulk-preview-list">
                <div className="bulk-preview-toolbar">
                  <span>{selectedCount}/{bulkEntries.length} selected</span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={toggleSelectAllBulk}
                    disabled={bulkSaving}
                  >
                    {allSelected ? 'Clear All' : 'Select All'}
                  </button>
                </div>
                {bulkEntries.map((entry, index) => {
                  const key = `${entry.wordEn}-${entry.wordFa}-${index}`;
                  const isSelected = Boolean(bulkSelectedKeys[key]);

                  return (
                    <label key={key} className={`bulk-preview-item${isSelected ? ' selected' : ''}${entry.duplicate ? ' duplicate' : ''}`}>
                      <input
                        className="bulk-preview-select"
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBulkSelection(entry, index)}
                        disabled={bulkSaving}
                      />
                      <div className="bulk-preview-content">
                        <strong>{entry.wordEn}</strong>
                        <span>→ {entry.wordFa}</span>
                        {entry.transliteration && (
                          <em>({entry.transliteration})</em>
                        )}
                        {entry.duplicate && (
                          <em className="bulk-preview-dup">already in your list</em>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          )}
          <div
            className="row-between"
            style={{marginTop: '4px'}}
          >
            <button
              type="button"
              className="btn"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={bulkSaving}
            >
              {selectedCount > 0 ? `Add ${selectedCount} Selected` : 'Add Word'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
